import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusyOutlined';
import { apiClient } from '@/api/axiosClient';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import TvKpiTile from '@/components/tvDashboard/TvKpiTile';
import TvTopList from '@/components/tvDashboard/TvTopList';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { topN } from '@/lib/dashboardAggregations';
import type { DashboardSummary, NearExpiryRow } from '@/types/reports';

const formatCurrency = (v: number) => `₹${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function OverviewSlide() {
  const { t } = useTranslation('dashboard');
  const colors = useCategoryColors();

  const { data } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: async () => (await apiClient.get<DashboardSummary>('/dashboard/summary')).data,
    refetchInterval: 60_000,
  });
  const { data: nearExpiry = [] } = useQuery({
    queryKey: ['reports', 'near-expiry'],
    queryFn: async () => (await apiClient.get<NearExpiryRow[]>('/reports/near-expiry', { params: { days: 30 } })).data,
    refetchInterval: 60_000,
  });

  const soonestExpiring = useMemo(
    () => topN(nearExpiry, (r) => -(r.daysToExpiry ?? Infinity), 5),
    [nearExpiry],
  );

  const pendingQueueChart = useMemo(
    () => ({
      categories: [t('pendingGrn'), t('pendingQc'), t('pendingPutaway'), t('pendingPickLists')],
      values: [data?.pendingGrnCount ?? 0, data?.pendingQcCount ?? 0, data?.pendingPutawayCount ?? 0, data?.pendingPickListCount ?? 0],
    }),
    [data, t],
  );

  const healthPie = useMemo(() => {
    const total = data?.totalMaterials ?? 0;
    const lowStock = data?.lowStockCount ?? 0;
    const nearExpiry = data?.nearExpiryCount ?? 0;
    const healthy = Math.max(total - lowStock - nearExpiry, 0);
    return [
      { label: t('overview.healthy'), value: healthy, color: colors.outbound },
      { label: t('lowStock'), value: lowStock, color: colors.production },
      { label: t('nearExpiry'), value: nearExpiry, color: colors.adjustments },
    ];
  }, [data, t, colors]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <TvKpiTile label={t('totalMaterials')} value={data?.totalMaterials ?? 0} icon={<Inventory2Icon />} accentColor={colors.overview} />
        <TvKpiTile label={t('inventoryValue')} value={formatCurrency(data?.totalStockValue ?? 0)} icon={<PaymentsIcon />} accentColor={colors.overview} />
        <TvKpiTile label={t('lowStock')} value={data?.lowStockCount ?? 0} icon={<WarningAmberIcon />} accentColor={colors.production} />
        <TvKpiTile label={t('nearExpiry')} value={data?.nearExpiryCount ?? 0} icon={<EventBusyIcon />} accentColor={colors.adjustments} />
      </Box>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1.1fr 0.8fr 0.9fr', gap: 2, minHeight: 0 }}>
        <ChartCard title={t('overview.pendingQueuesTitle')} subtitle={t('overview.pendingQueuesSubtitle')} height={420}>
          <DashboardBarChart
            categories={pendingQueueChart.categories}
            series={[{ label: t('overview.pendingItems'), data: pendingQueueChart.values, color: colors.overview }]}
            height={420}
            legendPosition="bottom"
          />
        </ChartCard>
        <ChartCard title={t('overview.stockHealthTitle')} subtitle={t('overview.stockHealthSubtitle')} height={420}>
          <DashboardPieChart data={healthPie} height={420} legendPosition="bottom" />
        </ChartCard>
        <TvTopList
          title={t('overview.soonestExpiringTitle')}
          accentColor={colors.adjustments}
          items={soonestExpiring.map((r) => {
            const days = r.daysToExpiry ?? 30;
            return { label: `${r.materialCode} — ${r.batchNo}`, value: Math.max(30 - days, 1), formattedValue: `${days}d` };
          })}
        />
      </Box>
    </Box>
  );
}
